import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, db } from './firebase';

const normaliseText = (value) => String(value || '').trim();

const parseMoney = (value) => {
  const amount = Number(String(value || '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(amount) ? amount : 0;
};

const orderingItemTotal = (item = {}) => {
  const fields = item.fields || {};
  const savedTotal = parseMoney(fields.itemTotal);
  if (savedTotal) return savedTotal;
  return parseMoney(fields.unitCost) * Math.max(Number(fields.orderedQuantity) || 0, 0);
};

const normaliseOrderFields = (fields = {}) => ({
  requiredBy: normaliseText(fields.requiredBy),
  status: normaliseText(fields.status) || 'Requested',
  notes: normaliseText(fields.notes),
});

const normaliseUpdateEntry = (update) => ({
  id: update.id || `update-${Date.now()}`,
  message: normaliseText(update.message),
  createdAt: update.createdAt || new Date().toISOString(),
  authorId: normaliseText(update.authorId),
  authorName: normaliseText(update.authorName),
  authorRole: normaliseText(update.authorRole),
});

const normaliseReceivingItem = (item = {}) => ({
  itemId: normaliseText(item.itemId),
  itemTitle: normaliseText(item.itemTitle),
  receivedQuantity: normaliseText(item.receivedQuantity),
  status: normaliseText(item.status),
  receivedDate: normaliseText(item.receivedDate),
  notes: normaliseText(item.notes),
  updatedAt: item.updatedAt || new Date().toISOString(),
});

const normaliseReceiving = (receiving = {}) => ({
  status: normaliseText(receiving.status) || 'Pending',
  items: Array.isArray(receiving.items) ? receiving.items.map(normaliseReceivingItem) : [],
  updatedAt: receiving.updatedAt || new Date().toISOString(),
});

const CONFIRMED_STATUS = 'Confirmed';
const CANCELLED_STATUS = 'Cancelled';

const receivingQuantity = (item = {}) => Math.max(Number(item.receivedQuantity) || 0, 0);

const applyReceivingStockAdjustment = async (transaction, response, direction) => {
  if (!response.linkId || !response.receiving?.items?.length) return;

  const itemRefs = response.receiving.items
    .filter((item) => item.itemId)
    .map((item) => ({
      item,
      ref: doc(db, 'sharabl-links', response.linkId, 'items', item.itemId),
    }));
  const itemSnapshots = await Promise.all(itemRefs.map(({ ref }) => transaction.get(ref)));

  itemRefs.forEach(({ item, ref }, index) => {
    const itemSnapshot = itemSnapshots[index];
    if (!itemSnapshot.exists()) return;

    const currentQuantity = Number(itemSnapshot.data().quantity) || 0;
    const nextQuantity = currentQuantity + (direction * receivingQuantity(item));

    if (nextQuantity < 0) {
      throw new Error(`Not enough quantity available for ${item.itemTitle || 'this item'}.`);
    }

    transaction.update(ref, { quantity: nextQuantity });
  });
};

export const itemFormService = {
  async listResponses() {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to view item form responses.');
    }

    const snapshot = await getDocs(query(
      collection(db, 'sharabl-item-form-responses'),
      where('ownerId', '==', auth.currentUser.uid),
    ));

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        submittedAt: data.submittedAt?.toDate ? data.submittedAt.toDate().toISOString() : data.submittedAt,
      };
    }).sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0));
  },

  async submitResponse(link, form, items, options = {}) {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to submit item forms.');
    }

    if (!link?.id || !form?.id) {
      throw new Error('This item form is missing a saved link or form reference.');
    }

    const isOrdering = form.preset === 'ordering';
    const normalisedItems = isOrdering
      ? items.map((item) => ({
        ...item,
        fields: {
          ...(item.fields || {}),
          itemTotal: orderingItemTotal(item).toFixed(2),
        },
      }))
      : items;

    const payload = {
      linkId: link.id,
      linkSlug: link.slug || '',
      linkTitle: link.title || '',
      formId: form.id,
      formTitle: form.title || '',
      preset: form.preset || '',
      ownerId: link.ownerId || auth.currentUser.uid,
      submittedBy: auth.currentUser.uid,
      items: normalisedItems,
      ...(isOrdering ? {
        orderFields: normaliseOrderFields(options.orderFields),
        orderTotal: normalisedItems.reduce((sum, item) => sum + orderingItemTotal(item), 0),
      } : {}),
      submittedAt: serverTimestamp(),
    };

    const created = await addDoc(collection(db, 'sharabl-item-form-responses'), payload);
    return { ...payload, id: created.id, submittedAt: new Date().toISOString() };
  },

  async updateResponse(responseOrId, changes = {}) {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to update item form responses.');
    }

    const id = typeof responseOrId === 'string' ? responseOrId : responseOrId?.id;

    if (!id) {
      throw new Error('This item form response is missing an ID.');
    }

    const allowedChanges = {};
    if (Array.isArray(changes.updates)) allowedChanges.updates = changes.updates.map(normaliseUpdateEntry);
    if (changes.receiving && typeof changes.receiving === 'object') {
      allowedChanges.receiving = normaliseReceiving(changes.receiving);
    }

    const responseRef = doc(db, 'sharabl-item-form-responses', id);

    if (allowedChanges.receiving) {
      await runTransaction(db, async (transaction) => {
        const responseSnapshot = await transaction.get(responseRef);
        if (!responseSnapshot.exists()) {
          throw new Error('Item form response not found.');
        }

        const response = { id, ...responseSnapshot.data() };
        const previousStatus = normaliseText(response.receiving?.status) || 'Pending';
        const nextStatus = allowedChanges.receiving.status || previousStatus;
        const nextResponse = {
          ...response,
          receiving: {
            ...(response.receiving || {}),
            ...allowedChanges.receiving,
          },
        };

        const shouldIncrementStock = previousStatus !== CONFIRMED_STATUS && nextStatus === CONFIRMED_STATUS;
        const shouldDecrementStock = previousStatus === CONFIRMED_STATUS && nextStatus === CANCELLED_STATUS;

        if (previousStatus === CONFIRMED_STATUS && nextStatus !== CONFIRMED_STATUS && nextStatus !== CANCELLED_STATUS) {
          throw new Error('A confirmed receiving record can only be cancelled.');
        }

        if (shouldIncrementStock) {
          await applyReceivingStockAdjustment(transaction, nextResponse, 1);
        } else if (shouldDecrementStock) {
          await applyReceivingStockAdjustment(transaction, response, -1);
        }

        transaction.update(responseRef, allowedChanges);
      });
    } else {
      await updateDoc(responseRef, allowedChanges);
    }

    return { id, ...allowedChanges };
  },

  async removeResponse(responseOrId) {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to remove item form responses.');
    }

    const id = typeof responseOrId === 'string' ? responseOrId : responseOrId?.id;

    if (!id) {
      throw new Error('This item form response is missing an ID.');
    }

    const responseRef = doc(db, 'sharabl-item-form-responses', id);

    await runTransaction(db, async (transaction) => {
      const responseSnapshot = await transaction.get(responseRef);
      if (!responseSnapshot.exists()) return;

      const response = { id, ...responseSnapshot.data() };
      if (normaliseText(response.receiving?.status) === CONFIRMED_STATUS) {
        await applyReceivingStockAdjustment(transaction, response, -1);
      }

      transaction.delete(responseRef);
    });

    return id;
  },
};
